import { graphql } from 'react-relay';
import type { FC } from 'react';
import React, { useCallback, useContext } from 'react';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { styled } from '@compiled/react';
import type { LoomVideo } from '@loomhq/record-sdk';

import { IconButton } from '@atlaskit/button/new';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Tooltip from '@atlaskit/tooltip';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import type { OpenLoomRecorderParams } from '@confluence/loom-utils/entry-points/useLoomRecorderEntryPoint.ts';
import { useLoomRecorderEntryPoint } from '@confluence/loom-utils/entry-points/useLoomRecorderEntryPoint.ts';
import { useEditPageLoadingActions } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext.ts';
import { getUrlForContentType } from '@confluence/content-types-utils/entry-points/getUrlForContentType.ts';
import { LOOM_PAGE_HEADER_CONTENT_INSERTION } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import type { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';

import { useLoomInsertUrlState } from '@confluence/loom-insert-url-store';
import { expVal } from '@confluence/feature-experiments';
import { useIsLoomAutoProvisionEnabled } from '@confluence/loom-utils/entry-points/useIsLoomAutoProvisionEnabled.ts';

import type {
	PageHeaderLoomButtonQuery$data,
	PageHeaderLoomButtonQuery$variables,
} from './__generated__/PageHeaderLoomButtonQuery.graphql.ts';
import { LoomVideoIcon } from './VideoIcon.tsx';
import { LoomSpotlightWrapper } from '@confluence/loom-utils/entry-points/LoomSpotlightWrapper.ts';
import { fg } from '@confluence/feature-gating';

type PageHeaderLoomButtonComponentProps = {
	contentId: string;
	dataVC?: string;
	label: string;
	spaceKey: string;
	contentType: string;
	flags: FlagsStateContainer;
	ssrPlaceholderIdProp?: ReturnType<typeof useSSRPlaceholderReplaceIdProp>;
	isCircle?: boolean;
	testId?: string;
	source?: string;
	isDisabled?: boolean;
};

export enum PostLoomRecordingCTA {
	EDITOR_INSERT = 'editorInsert',
	PAGE_COMMENT_INSERT = 'pageCommentInsert',
	COPY_LINK = 'copyLink',
	OPEN_ON_LOOM = 'openOnLoom',
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonSpacer = styled.div({
	minWidth: '30px',
});

const PageHeaderLoomButtonComponent: FC<PageHeaderLoomButtonComponentProps> = ({
	contentId,
	dataVC,
	label,
	spaceKey,
	contentType,
	flags,
	ssrPlaceholderIdProp,
	isCircle = false,
	testId,
	source = 'pageHeader',
	isDisabled = false,
}) => {
	const { userId } = useSessionData();
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { loadEditor } = useEditPageLoadingActions();
	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [, loomInsertUrlActions] = useLoomInsertUrlState();
	const pageMode = useGetPageMode();
	const { isLoomAutoProvisionEnabled, setSeenSpotlight } = useIsLoomAutoProvisionEnabled();

	const saveLoomUrlAndLoadEditor = (video: LoomVideo) => {
		loomInsertUrlActions.updateFullPageEditorLoomUrl(video);
		const redirectUrl = getUrlForContentType({
			contentType,
			spaceKey,
			contentId,
			redirectToEditor: true,
		});
		loadEditor({
			contentId,
			spaceKey,
			redirectUrl,
			contentType,
		});
	};

	const beginExperienceTracker = (postLoomRecordingCTA: PostLoomRecordingCTA) => {
		experienceTracker.start({
			name: LOOM_PAGE_HEADER_CONTENT_INSERTION,
			timeout: ExperienceTimeout.LOOM_PAGE_HEADER_CONTENT_INSERTION,
			attributes: { postLoomRecordingCTA },
		});
	};

	const { data } = useQueryInterop<
		PageHeaderLoomButtonQuery$data,
		PageHeaderLoomButtonQuery$variables
	>(
		graphql`
			query PageHeaderLoomButtonQuery($contentId: ID!) {
				contentContributors(id: $contentId) {
					isCurrentUserContributor
				}
				content(id: $contentId, status: ["current", "draft"]) {
					nodes {
						id
						operations {
							operation
							targetType
						}
						history {
							ownedBy {
								... on KnownUser {
									accountId
								}
							}
						}
					}
				}
			}
		`,
		{
			variables: { contentId },
		},
	);

	let openLoomRecorderParams: OpenLoomRecorderParams | undefined;
	let postLoomRecordingCTA: PostLoomRecordingCTA = PostLoomRecordingCTA.OPEN_ON_LOOM;
	let resetInsertFunctionOnUnmount = false;

	const contentData = data?.content?.nodes?.[0];

	const isContentContributor =
		data?.contentContributors?.isCurrentUserContributor ||
		// isCurrentUserContributor is false for unpublished drafts, so we also check the owner
		(contentData?.history?.ownedBy as any)?.accountId === userId;

	const canEdit = contentData?.operations?.some(
		(operation) => operation?.operation === 'update' && operation?.targetType === contentType,
	);
	const canComment = contentData?.operations?.some(
		(operation) => operation?.targetType === 'comment' && operation?.operation === 'create',
	);

	if (canEdit && isContentContributor && contentType !== 'database' && contentType !== 'slide') {
		// editor mode insert
		postLoomRecordingCTA = PostLoomRecordingCTA.EDITOR_INSERT;
		openLoomRecorderParams = {
			onInsert: (_, video: LoomVideo) => {
				beginExperienceTracker(postLoomRecordingCTA);
				saveLoomUrlAndLoadEditor(video);
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Insert Loom video',
		};
	} else if (canComment && contentType !== 'database' && contentType !== 'slide') {
		// page comment insert
		postLoomRecordingCTA = PostLoomRecordingCTA.PAGE_COMMENT_INSERT;
		// Set to true so that CTA 'Add to comment' will not render if user has navigated to
		// different page post Loom recording.
		resetInsertFunctionOnUnmount = true;
		openLoomRecorderParams = {
			onInsert: (_, video: LoomVideo) => {
				beginExperienceTracker(postLoomRecordingCTA);
				loomInsertUrlActions.updatePageCommentEditorLoomUrl(video);
				const openPageCommentEditorButton = document.getElementById('addCommentButton');
				openPageCommentEditorButton && openPageCommentEditorButton.click();
			},
			// intentionally not localized because Loom RecordSDK does not support i18n
			insertButtonText: 'Add to comment',
		};
	} else {
		postLoomRecordingCTA = PostLoomRecordingCTA.COPY_LINK;
		// Not defining openLoomRecorderParams here so that
		// undefined will be passed to the Loom SDK resulting
		// in the Loom SDK rendering a single CTA of 'Copy link'
	}

	const { openLoomRecorder, isOpeningRecorder } = useLoomRecorderEntryPoint({
		entryPointLocation: 'pageHeader',
		flags,
		resetInsertFunctionOnUnmount,
	});

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'createLoomHeaderItem',
				source,
				attributes: {
					postLoomRecordingCTA,
					contentType,
					pageMode,
				},
			},
		}).fire();

		void openLoomRecorder(openLoomRecorderParams);
		if (isLoomAutoProvisionEnabled && fg('loom_post_ap_add_spotlight_to_confluence_couse')) {
			setSeenSpotlight();
		}
	}, [
		createAnalyticsEvent,
		openLoomRecorder,
		openLoomRecorderParams,
		postLoomRecordingCTA,
		source,
		pageMode,
		contentType,
		isLoomAutoProvisionEnabled,
		setSeenSpotlight,
	]);

	if (isLoomAutoProvisionEnabled && fg('loom_post_ap_add_spotlight_to_confluence_couse')) {
		// Render with Spotlight wrapper when isLoomAutoProvisionEnabled is true and feature gate is enabled
		const baseLoomIconButton = isCircle ? (
			<IconButton
				label={label}
				isLoading={isOpeningRecorder}
				onClick={handleClick}
				appearance="subtle"
				shape="circle"
				icon={LoomVideoIcon}
				testId={testId}
				isDisabled={isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)}
			/>
		) : (
			<IconButton
				appearance="subtle"
				icon={LoomVideoIcon}
				label={label}
				onClick={handleClick}
				isLoading={isOpeningRecorder}
				data-vc={dataVC}
				{...ssrPlaceholderIdProp}
				testId={testId}
				isDisabled={isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)}
			/>
		);

		return (
			<LoomSpotlightWrapper
				onSpotlightPrimaryActionClick={handleClick}
				onSpotlightClose={setSeenSpotlight}
				touchpoint="page-header"
			>
				<ButtonSpacer>{baseLoomIconButton}</ButtonSpacer>
			</LoomSpotlightWrapper>
		);
	}

	// Default render with Tooltip
	const baseLoomButton = (
		<Tooltip content={label} position="left">
			{(tooltipProps) => (
				<ButtonSpacer>
					{isCircle ? (
						<IconButton
							label={label}
							{...tooltipProps}
							isLoading={isOpeningRecorder}
							onClick={handleClick}
							appearance="subtle"
							shape="circle"
							icon={LoomVideoIcon}
							testId={testId}
							isDisabled={
								isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)
							}
						/>
					) : (
						<IconButton
							appearance="subtle"
							icon={LoomVideoIcon}
							label={label}
							{...tooltipProps}
							onClick={handleClick}
							isLoading={isOpeningRecorder}
							data-vc={dataVC}
							{...ssrPlaceholderIdProp}
							testId={testId}
							isDisabled={
								isDisabled && expVal('platform_editor_offline_editing_web', 'isEnabled', false)
							}
						/>
					)}
				</ButtonSpacer>
			)}
		</Tooltip>
	);

	return baseLoomButton;
};

export const PageHeaderLoomButton = withFlags(PageHeaderLoomButtonComponent);
